import type { IMapLayer, IMetaMapLayer, I3DTilesetsLayer, IPointCloudTilesetsLayer } from '$lib/models/IMapLayer';
import { tabletModeStore } from '$lib/stores/tablet-mode-store';
import clonedeep from 'lodash.clonedeep';
import { get, writable } from 'svelte/store';
import {
	getWMSImageryMetaProviders,
	CULTURAL_HERITAGE_META_LAYER_NAME,
	CADASTRE_QUERY_LAYERS
} from './WMS-meta-layers';
import { getWMTSImageryMapLayers, getWMTSOrtofotoAsync /*, getNibLayer */ } from './WMTS-map-layers';
import { get3DTilesMapLayers } from './Tiles3D-map-layers';
import { installMapServerFeaturePicking, NATIVE_TOLERANCE_PX } from './wms-feature-info';

const {
	ar5ArealtypeWMS,
	cadastreSimpleWMS,
	culturalHeritageWMSGroup,
	omradekonsesjonaererWMS,
	nettanleggWMS,
	masterOgStolperWMS,
	transformerWMS,
	quickClayWMS,
	skredJordFlomAktR1WMS,
	nveFlomaktsomhetWMS,
	nveSkredSnoAktRWMS,
	nveSkredSteinAktRRWMS,
	nguGranada5WMS,
	planomraadeWMS
} = getWMSImageryMetaProviders();

const { topo4GraaLayer, topo4Layer } = getWMTSImageryMapLayers();

interface IMapLayersStore {
	mapLayerCollection: IMapLayer[];
	metaLayerCollection: IMetaMapLayer[];
	mapLayer3DTileSets: I3DTilesetsLayer[];
	superAdmin3DTileSets: IPointCloudTilesetsLayer[];
	displayedMapLayerCount: number;
	displayedMetaLayerCount: number;
	orderOfVisibleMetaLayersByName: string[];
	aviationObstaclesRedDisplay: boolean;
	aviationObstaclesBlueDisplay: boolean;
}

const initialStore: IMapLayersStore = {
	displayedMapLayerCount: get(tabletModeStore).valueOf() ? 0 : 2,
	displayedMetaLayerCount: 0,
	orderOfVisibleMetaLayersByName: [],
	mapLayerCollection: [
		{
			name: 'Topo4 Grayscale',
			imageryLayer: topo4GraaLayer,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/f004268c-d4a1-4801-91cb-daa46236fab7'
		},
		/*{
			name: 'Norge i Bilder',
			imageryLayer: getNibLayer(),
			mapOptions: {
				displayLayerOnMap: !get(tabletModeStore).valueOf(), // Automatically display only if the user is not in tablet/field mode.
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/nasjonal-hoeydemodell-digital-terrengmodell-25832-wcs/05821c51-2f5b-411f-9098-924d13dbea9a'
		},*/
		{
			name: 'Topo4 Colored',
			imageryLayer: topo4Layer,
			mapOptions: {
				displayLayerOnMap: !get(tabletModeStore).valueOf(), // Automatically display only if the user is not in tablet/field mode.
				layerOpacityLevel: 0.5
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/f004268c-d4a1-4801-91cb-daa46236fab7'
		}
	],
	metaLayerCollection: [
		{
			name: 'Arealplaner',
			imageryLayer: [planomraadeWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/planomraade-wms/d70cedfc-fdb2-45cc-a9c6-0b7f09a1e746'
		},
		{
			name: CULTURAL_HERITAGE_META_LAYER_NAME,
			imageryLayer: culturalHeritageWMSGroup,
			cesiumGeneratedImageryProvider: null,
			installPickFeatures: installMapServerFeaturePicking(),
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/kulturminner-wms/30369f29-e21a-464f-97f7-a202ca7c97e7'
		},
		{
			name: 'Matrikkel',
			imageryLayer: [cadastreSimpleWMS],
			cesiumGeneratedImageryProvider: null,
			// Cadastre layers are scale-gated, so the query has to run at the display scale.
			installPickFeatures: installMapServerFeaturePicking({
				queryLayers: CADASTRE_QUERY_LAYERS,
				tolerancePx: NATIVE_TOLERANCE_PX
			}),
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/matrikkelen-enkel-wms/0fa5fa99-27ce-49fc-adb1-551b93b7b4d4'
		},
		{
			name: 'NGU - Borehull - dyp til fjell',
			imageryLayer: [nguGranada5WMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/grunnvann-granada-wms/4a0f9676-7cd0-4555-a8f3-763bb769d5d4'
		},
		{
			name: 'NIBIO Arealtype AR5',
			imageryLayer: [ar5ArealtypeWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink: 'https://www.nibio.no/tjenester/wms-tjenester/wms-tjenester-ar5'
		},
		/* 		{
			name: 'NVE Distribusjonsnett',
			imageryLayer: [distributionWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/nettanlegg-wms/ac9533a0-03e7-4127-8e96-109a20ab6b9c'
		}, */
		{
			name: 'NVE Flomaktsomhet',
			imageryLayer: [nveFlomaktsomhetWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/flom-aktsomhetsomraader-wms/834179b8-d189-4bc0-b00f-533ffe80faed'
		},
		{
			name: 'NVE Jord- og flomskred - aktsomhetskart',
			imageryLayer: [skredJordFlomAktR1WMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://nve.geodataonline.no/arcgis/rest/services/SkredJordFlomAktR1/MapServer'
		},
		{
			name: 'NVE Kvikkleire',
			imageryLayer: [quickClayWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/kvikkleire-wms/f8007522-2b73-4702-bb97-50c720e5c3f8'
		},
		{
			name: 'NVE Master og stolper',
			imageryLayer: [masterOgStolperWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/nettanlegg-wms/ac9533a0-03e7-4127-8e96-109a20ab6b9c'
		},
		{
			name: 'NVE Nettanlegg',
			imageryLayer: [nettanleggWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/nettanlegg-wms/ac9533a0-03e7-4127-8e96-109a20ab6b9c'
		},
		{
			name: 'NVE Områdekonsesjonærer',
			imageryLayer: [omradekonsesjonaererWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/nettanlegg-wms/ac9533a0-03e7-4127-8e96-109a20ab6b9c'
		},
		{
			name: 'NVE Snøskred -aktsomhetsområder',
			imageryLayer: [nveSkredSnoAktRWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink: ''
		},
		{
			name: 'NVE Steinsprang -aktsomhetsområder',
			imageryLayer: [nveSkredSteinAktRRWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink: ''
		},
		{
			name: 'NVE Transformatorstasjoner',
			imageryLayer: [transformerWMS],
			cesiumGeneratedImageryProvider: null,
			mapOptions: {
				displayLayerOnMap: false,
				layerOpacityLevel: 1
			},
			layerProviderInfoLink:
				'https://kartkatalog.geonorge.no/metadata/nettanlegg-wms/ac9533a0-03e7-4127-8e96-109a20ab6b9c'
		}
	],
	mapLayer3DTileSets: [],
	superAdmin3DTileSets: [],
	aviationObstaclesRedDisplay: false,
	aviationObstaclesBlueDisplay: false
};

function createMapLayersStore() {
	const { subscribe, set, update } = writable<IMapLayersStore>(clonedeep(initialStore));

	return {
		subscribe,
		set,
		update,
		reset: () =>
			update((store) => {
				store = clonedeep(initialStore);
				return store;
			}),
		async init() {
			// Independent network work — run it concurrently so the ortofoto layer is
			// not held up behind the two 3D tilesets.
			const [addAsyncMapLayer, { norkartForestryTileset, norkartObjectsTileset }] =
				await Promise.all([getWMTSOrtofotoAsync(), get3DTilesMapLayers()]);

			const norkartOrtofoto = {
				name: 'Norkart Ortofoto',
				imageryLayer: addAsyncMapLayer,
				mapOptions: {
					displayLayerOnMap: !get(tabletModeStore).valueOf(),
					layerOpacityLevel: 1
				},
				layerProviderInfoLink: 'https://norkart.no/'
			};

			const norkart3DLayers = [
				{
					name: 'Skog',
					tileSetProvider: norkartForestryTileset,
					mapOptions: {
						displayLayerOnMap: false
					}
				},
				{
					name: 'Bygninger/Objekter',
					tileSetProvider: norkartObjectsTileset,
					mapOptions: {
						displayLayerOnMap: false
					}
				}
			];
			update((store) => {
				store.mapLayerCollection = [...store.mapLayerCollection, norkartOrtofoto];
				store.mapLayer3DTileSets = norkart3DLayers;
				return store;
			});
		}
	};
}

export const mapLayersStore = createMapLayersStore();